import type { GameListItemDto } from '@/queries/games/games.schema';
import { useEffect, useRef } from 'react';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/utils/cn';
import EmptyGamesState from '../EmptyGamesState';
import ResentSearches from '../ResentSearches';
import FilterHeader from './components/FilterHeader';
import ListOfGames from './components/ListOfGames';
import PopularGames from './components/TopGames';
import { useDropdownMaxHeight } from './hooks/useDropdownMaxHeight';

interface Props {
  searchValue: string;
  isFocused: boolean;
  recentSearch: string;
  gameList: GameListItemDto[];
  isLoadingGames: boolean;
  blurSearchInput: () => void;
}
const MobileSearchDropdown = ({
  searchValue,
  isFocused,
  recentSearch,
  gameList,
  isLoadingGames,
  blurSearchInput,
}: Props) => {
  const lastAutoBlurredQueryRef = useRef<string>('');
  const isMobile = useIsMobile();
  const maxHeightPx = useDropdownMaxHeight();

  const hasSearchValue = Boolean(searchValue);
  const hasGames = gameList.length > 0;
  const showResults = hasSearchValue && (hasGames || isLoadingGames);

  useEffect(() => {
    if (!isMobile || !isFocused || isLoadingGames) {
      return;
    }

    if (!searchValue) {
      lastAutoBlurredQueryRef.current = '';
      return;
    }

    if (lastAutoBlurredQueryRef.current === searchValue) {
      return;
    }

    blurSearchInput();
    lastAutoBlurredQueryRef.current = searchValue;
  }, [isMobile, isFocused, isLoadingGames, blurSearchInput, searchValue]);

  const maxHeightStyle = maxHeightPx !== null ? { maxHeight: maxHeightPx } : undefined;

  return (
    <div
      className={cn(
        'lg:hidden z-50 mt-1 bg-c900 rounded-2xl shadow-lg p-4 md:p-6 max-w-full min-h-25 absolute w-full hide-scrollbar flex flex-col overflow-y-auto overscroll-contain max-h-[70dvh]',
        hasSearchValue && !isLoadingGames && !hasGames && 'pt-0!',
      )}
      style={maxHeightStyle}
    >
      {hasSearchValue && (
        <div className="flex flex-1 flex-col">
          {showResults
            ? (
                <>
                  <FilterHeader isLoading={isLoadingGames} />
                  <ListOfGames isLoading={isLoadingGames} filteredGames={gameList} />
                </>
              )
            : (
                <>
                  <div className="flex flex-1 items-start justify-center">
                    <EmptyGamesState isCompactSize />
                  </div>
                </>
              )}
        </div>
      )}

      {!hasGames && recentSearch.length > 0 && isFocused && (
        <ResentSearches recentSearch={recentSearch} />
      )}

      <PopularGames />
    </div>
  );
};
export default MobileSearchDropdown;
